import { IChartApi, UTCTimestamp } from 'lightweight-charts';

/**
 * Convert timeframe string to milliseconds
 * @param timeframe - e.g., "1m", "5m", "15m", "1h", "4h", "1d"
 * @returns milliseconds
 */
export const parseTimeframeToMs = (timeframe: string): number => {
  const match = timeframe.match(/^(\d+)([mhd])$/);
  if (!match) {
    throw new Error(`Invalid timeframe format: ${timeframe}`);
  }

  const value = parseInt(match[1], 10);
  const unit = match[2];

  switch (unit) {
    case 'm': // minutes
      return value * 60 * 1000;
    case 'h': // hours
      return value * 60 * 60 * 1000;
    case 'd': // days
      return value * 24 * 60 * 60 * 1000;
    default:
      throw new Error(`Unsupported timeframe unit: ${unit}`);
  }
};

/**
 * Adjust time to the nearest candlestick boundary for the target timeframe
 * @param originalTime - Unix timestamp in seconds
 * @param targetTimeframe - e.g., "1m", "5m", "15m", "1h", "4h"
 * @returns adjusted Unix timestamp in seconds
 */
export const adjustTimeForTimeframe = (
  originalTime: number,
  targetTimeframe: string
): number => {
  const originalTimeMs = originalTime * 1000; // Convert to milliseconds
  const timeframeMs = parseTimeframeToMs(targetTimeframe);

  // Round down to the nearest timeframe boundary
  const adjustedTimeMs = Math.floor(originalTimeMs / timeframeMs) * timeframeMs;

  return adjustedTimeMs / 1000; // Convert back to seconds
};

/**
 * Check if a time adjustment is needed between source and target timeframes
 * @param sourceTimeframe - Source timeframe (e.g., "5m")
 * @param targetTimeframe - Target timeframe (e.g., "4h")
 * @returns true if adjustment is needed
 */
export const needsTimeAdjustment = (
  sourceTimeframe: string,
  targetTimeframe: string
): boolean => {
  if (!sourceTimeframe || !targetTimeframe) return false;
  return sourceTimeframe !== targetTimeframe;
};

export type ChartTime =
  | number
  | { year: number; month: number; day: number }
  | string
  | null
  | undefined;

/**
 * Narrow `ChartTime` to numeric UTC seconds (no value adjustments).
 * - number -> returned as-is
 * - numeric string -> Number(string)
 * - otherwise -> null
 */
export function timeToTimestamp(time: ChartTime): UTCTimestamp | null {
  if (time == null) return null;
  if (typeof time === 'number') return time as UTCTimestamp;
  if (typeof time === 'string') {
    const numeric = Number(time);
    return Number.isFinite(numeric) ? (numeric as UTCTimestamp) : null;
  }
  return null;
}

/**
 * Attempt to map time to coordinate directly.
 */
export function safeTimeToCoordinate(
  chart: IChartApi,
  time: ChartTime
): number | null {
  if (time == null) return null;
  try {
    return chart.timeScale().timeToCoordinate(time as any);
  } catch {
    return null;
  }
}

/**
 * Convert ms timestamp to seconds if needed.
 * (A LOT of FX CSV data comes as ms.)
 */
export function toSecondsIfMs(t: ChartTime): ChartTime {
  if (typeof t !== 'number') return t;
  return t > 1e12 ? Math.floor(t / 1000) : t;
}

/**
 * Find a coordinate for the nearest *existing* bar time on the target chart.
 *
 * Why this is needed:
 * - adjustTimeForTimeframe rounds to epoch boundaries
 * - but bar opens can be offset (broker/session timezone), and weekends missing
 * - so `timeToCoordinate()` can still return null
 *
 * Strategy:
 * - Look at visible logical range
 * - Binary-ish scan by sampling logical indices around the estimated position
 * - Pick the closest time we can map
 */
function snapToNearestExistingBar(
  chart: IChartApi,
  targetTimeSec: number
): { x: number | null; snappedTime?: number } {
  const ts = chart.timeScale();

  // If we can map the exact time, we're done.
  const direct = safeTimeToCoordinate(chart, targetTimeSec);
  if (direct != null) return { x: direct, snappedTime: targetTimeSec };

  // Visible range gives us the logical indices currently on screen.
  const vr = ts.getVisibleLogicalRange?.();
  if (!vr) return { x: null };

  // Clamp helper
  const clamp = (v: number, a: number, b: number) =>
    Math.max(a, Math.min(b, v));

  const from = Math.floor(vr.from);
  const to = Math.ceil(vr.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from)
    return { x: null };

  // We'll sample logical positions and infer their times via coordinateToTime if available,
  // otherwise we fall back to probing by coordinates.
  const width = ts.width?.() ?? 0;
  if (!width) {
    // no width yet -> can't do coordinate-based probing
    return { x: null };
  }

  // Helper: get time at a logical index by converting logical->coordinate->time
  const timeAtLogical = (logical: number): number | null => {
    const coord = ts.logicalToCoordinate(logical as any);
    if (coord == null) return null;

    // Some versions have coordinateToTime
    const ctt = (ts as any).coordinateToTime?.bind(ts);
    if (typeof ctt === 'function') {
      const t = ctt(coord);
      // returns Time (could be BusinessDay); we only handle numeric timestamps here
      return typeof t === 'number' ? t : null;
    }

    // If coordinateToTime is unavailable, we can't reliably retrieve the time.
    // In that case, we can still try to snap by using logicalToCoordinate directly (we already have coord),
    // but we need a time to compare distances => bail.
    return null;
  };

  // If coordinateToTime isn't supported, we can't compute "nearest by time".
  // We’ll do a simpler fallback: try neighbors around the *estimated* logical position
  // by searching for any logical that produces a valid coordinate (it will),
  // then use that coordinate even if time differs.
  const hasCoordinateToTime =
    typeof (ts as any).coordinateToTime === 'function';

  // Estimate logical position: use timeToCoordinate on a close-by time?
  // We don't have a coordinate, so estimate by mapping the target time to a percentage of visible range.
  // We'll just start from the middle and search outward.
  let startLogical = Math.floor((from + to) / 2);

  // If we can infer times, do a guided search by time distance
  if (hasCoordinateToTime) {
    // Sample a small set of logicals to get a rough mapping
    const samples = 24; // keep small for perf
    let best: {
      logical: number;
      x: number;
      time: number;
      dist: number;
    } | null = null;

    for (let i = 0; i <= samples; i++) {
      const logical = Math.floor(from + (i * (to - from)) / samples);
      const t = timeAtLogical(logical);
      if (t == null) continue;

      const x = ts.logicalToCoordinate(logical as any);
      if (x == null) continue;

      const dist = Math.abs(t - targetTimeSec);
      if (!best || dist < best.dist) best = { logical, x, time: t, dist };
    }

    if (best) {
      // Now refine around best.logical
      const refineRadius = 50; // scan nearby bars
      const rFrom = clamp(best.logical - refineRadius, from, to);
      const rTo = clamp(best.logical + refineRadius, from, to);

      for (let logical = rFrom; logical <= rTo; logical++) {
        const t = timeAtLogical(logical);
        if (t == null) continue;
        const x = ts.logicalToCoordinate(logical as any);
        if (x == null) continue;

        const dist = Math.abs(t - targetTimeSec);
        if (dist < best.dist) best = { logical, x, time: t, dist };
      }

      return { x: best.x, snappedTime: best.time };
    }
  }

  // Fallback (no coordinateToTime): just pick the nearest logical to center and use its coord
  // This will at least show the crosshair, but time label may not match perfectly.
  // We search outwards for a valid coordinate (should always exist).
  const maxSteps = Math.min(2000, to - from);
  for (let step = 0; step <= maxSteps; step++) {
    for (const sign of [0, -1, 1]) {
      const logical = clamp(startLogical + sign * step, from, to);
      const x = ts.logicalToCoordinate(logical as any);
      if (x != null) return { x };
    }
  }

  return { x: null };
}

/**
 * Given an incoming time, return a coordinate on the target chart, snapping if needed.
 * - fixes ms->sec
 * - applies your timeframe rounding when requested
 * - snaps to nearest existing bar if timeToCoordinate returns null
 */
export function resolveXPos(
  chart: IChartApi,
  time: ChartTime,
  targetTf?: string
): { x: number | null; usedTime?: number } {
  if (time == null) return { x: null };

  // We only handle numeric seconds here; if your system sometimes emits BusinessDay/string,
  // you can extend this. Your adjustTimeForTimeframe expects number seconds.
  let t = toSecondsIfMs(time);
  if (typeof t !== 'number') {
    // Try mapping non-numeric directly
    const x = safeTimeToCoordinate(chart, t);
    return { x };
  }

  let adjusted = t;
  if (targetTf) {
    adjusted = adjustTimeForTimeframe(t, targetTf); // your function: seconds in/out
  }

  const direct = safeTimeToCoordinate(chart, adjusted);
  if (direct != null) return { x: direct, usedTime: adjusted };

  // Snap to nearest existing bar on this chart
  return snapToNearestExistingBar(chart, adjusted);
}
